import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ToastContext } from '@/components/common/CommonToast';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { toastErrorMessage } from '@/lib/utils';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import PATH from '@/routes/path';
import { UserColumns } from '../table/UserColumns';

type Props = {
  selectedRows: Record<number, boolean>[];
  setSelectedRows: (selectedRows: any) => void;
  changeStatusUser: string;
  searchTrigger: number;
};

const UserManagementList: React.FC<Props> = ({
  selectedRows,
  setSelectedRows,
  changeStatusUser,
  searchTrigger,
}: Props) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const navigate = useNavigate();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());

  const { data: users, error } = useFetchClient(
    async () => await apiService.usersCoachRequest.query(query, params),
    [query, changeStatusUser, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    users?.pagination,
  );

  async function handleEditUser(id: number) {
    navigate(`${PATH.user_management}/${PATH.user_management_edit}/${id}`);
  }

  function onPaginationChanged(paginationState: Pagination) {
    setSelectedRows([]);
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  function onSortChanged(paginationState: Pagination) {
    setSelectedRows([]);
    setPagination(paginationState);
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[20px]"
        values={users?.data || []}
        selectable
        columns={UserColumns(t, handleEditUser, paginationComputed)}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
        selection={selectedRows}
        onSelectionChange={(e) => {
          setSelectedRows(e);
        }}
        selectionMode="checkbox"
      />
    </>
  );
};

export default UserManagementList;
